// A tiny formant speech synthesizer for offline rendering (§13.5 bean gibberish, §13.3 announcer).
// A phone is a set of formant targets (F1–F3), a voicing level, an aspiration level (noise through
// the formants, for 'h') and a frication noise band (s, t, k bursts). Phones are glued with short
// linear transitions; the source is a saw (beans) or the §13.3 "robotic" carrier (saw 110 Hz plus a
// square an octave up at −12 dB).
import type { Kit } from '../sfx/kit';

export interface Phone {
  f: readonly [number, number, number];
  /** Voiced source level 0..1. */
  voice: number;
  /** Aspiration (noise through the formants) 0..1. */
  asp: number;
  /** Frication noise level and band. */
  fric: number;
  fricHz: number;
  fricQ: number;
  dur: number;
  /** Transition into this phone, seconds. */
  glide: number;
}

const V = (f1: number, f2: number, f3: number, dur = 0.12): Phone => ({ f: [f1, f2, f3], voice: 1, asp: 0, fric: 0, fricHz: 3000, fricQ: 1, dur, glide: 0.035 });

/** Vowel targets (§13.2 pairs for a/e/i/o/u, typical values for the rest). */
export const VOWELS = {
  a: V(800, 1150, 2800), e: V(400, 1600, 2600), i: V(280, 2250, 3000), o: V(450, 800, 2830), u: V(325, 700, 2530),
  schwa: V(500, 1500, 2500), ae: V(660, 1700, 2400), ih: V(400, 1900, 2550), uh: V(600, 1170, 2400), aw: V(570, 840, 2410),
} as const;
export type VowelName = keyof typeof VOWELS;

function c(p: Partial<Phone> & { dur: number }): Phone {
  return { f: [300, 1500, 2500], voice: 0, asp: 0, fric: 0, fricHz: 3000, fricQ: 1, glide: 0.012, ...p };
}

/** Consonants. Stops are a closure (silence or voice bar) followed by a burst phone. */
export const CONS = {
  b: [c({ f: [200, 900, 2200], voice: 0.25, dur: 0.035 }), c({ f: [250, 1000, 2300], voice: 0.6, fric: 0.25, fricHz: 600, fricQ: 0.8, dur: 0.012 })],
  d: [c({ f: [200, 1600, 2600], voice: 0.25, dur: 0.03 }), c({ f: [250, 1700, 2600], voice: 0.5, fric: 0.4, fricHz: 3500, fricQ: 1.2, dur: 0.014 })],
  g: [c({ f: [200, 1900, 2300], voice: 0.25, dur: 0.03 }), c({ f: [260, 1900, 2400], voice: 0.5, fric: 0.45, fricHz: 2000, fricQ: 1.5, dur: 0.018 })],
  p: [c({ dur: 0.04 }), c({ f: [400, 1100, 2400], fric: 0.55, fricHz: 900, fricQ: 0.6, asp: 0.3, dur: 0.03 })],
  t: [c({ dur: 0.035 }), c({ f: [400, 1700, 2600], fric: 0.7, fricHz: 4200, fricQ: 1.4, asp: 0.3, dur: 0.035 })],
  k: [c({ dur: 0.035 }), c({ f: [400, 1800, 2500], fric: 0.7, fricHz: 2100, fricQ: 2, asp: 0.35, dur: 0.04 })],
  s: [c({ fric: 0.6, fricHz: 6500, fricQ: 2.5, dur: 0.09 })],
  sh: [c({ fric: 0.6, fricHz: 3200, fricQ: 2, dur: 0.09 })],
  ch: [c({ dur: 0.03 }), c({ fric: 0.7, fricHz: 3300, fricQ: 2, dur: 0.07 })],
  th: [c({ fric: 0.22, fricHz: 6000, fricQ: 0.6, dur: 0.07 })],
  f: [c({ fric: 0.22, fricHz: 5000, fricQ: 0.5, dur: 0.07 })],
  h: [c({ asp: 0.75, dur: 0.05, glide: 0.005 })],
  m: [c({ f: [250, 1100, 2300], voice: 0.55, dur: 0.07, glide: 0.02 })],
  n: [c({ f: [250, 1700, 2600], voice: 0.55, dur: 0.06, glide: 0.02 })],
  ng: [c({ f: [250, 2000, 2700], voice: 0.5, dur: 0.06, glide: 0.02 })],
  l: [c({ f: [360, 1000, 2800], voice: 0.75, dur: 0.06, glide: 0.025 })],
  r: [c({ f: [420, 1300, 1650], voice: 0.75, dur: 0.06, glide: 0.03 })],
  w: [c({ f: [300, 610, 2200], voice: 0.7, dur: 0.05, glide: 0.03 })],
  y: [c({ f: [260, 2200, 3000], voice: 0.7, dur: 0.045, glide: 0.025 })],
  gap: [c({ dur: 0.03 })],
} as const;
export type ConsName = keyof typeof CONS;

/** A sequence token: a vowel name with optional duration, or a consonant. */
export type Token = ConsName | VowelName | [VowelName, number];

export function phones(seq: readonly Token[]): Phone[] {
  const out: Phone[] = [];
  for (const t of seq) {
    if (Array.isArray(t)) out.push({ ...VOWELS[t[0]], dur: t[1] });
    else if (t in VOWELS) out.push({ ...VOWELS[t as VowelName] });
    else out.push(...CONS[t as ConsName].map((p) => ({ ...p })));
  }
  return out;
}

export interface SpeakOptions {
  /** [time, Hz] pitch keyframes (relative to `at`). */
  pitch: readonly (readonly [number, number])[];
  carrier: 'bean' | 'robot';
  at?: number;
  gain?: number;
  /** Vibrato rate and depth (cents). */
  vibrato?: [number, number];
  /** Scales every formant (a smaller vocal tract sounds younger/cuter). */
  formantScale?: number;
}

/** Total duration of a phone sequence. */
export function phonesDuration(ps: readonly Phone[]): number {
  return ps.reduce((s, p) => s + p.dur, 0);
}

/** Bean voice pre-emphasis (high shelf). */
const PRE_EMPHASIS_HZ = 1400;
const PRE_EMPHASIS_DB = 12;

/** Renders the phones into `k.out`; returns the end time. */
export function speak(k: Kit, ps: readonly Phone[], o: SpeakOptions): number {
  const at = o.at ?? 0;
  const total = phonesDuration(ps);
  const end = at + total;
  const fs = o.formantScale ?? 1;

  // Sources.
  const voiced = k.gain(1);
  const p0 = o.pitch[0]?.[1] ?? 220;
  const main = k.osc('sawtooth', p0, at, end + 0.05);
  const pitchParams: AudioParam[] = [main.frequency];
  main.connect(voiced);
  if (o.carrier === 'robot') {
    const sq = k.osc('square', p0 * 2, at, end + 0.05);
    const g = k.gain(0.25);
    sq.connect(g).connect(voiced);
    pitchParams.push(sq.frequency);
  }
  for (const [t, hz] of o.pitch) {
    pitchParams.forEach((param, i) => param.linearRampToValueAtTime(hz * (i === 0 ? 1 : 2), at + t));
  }
  if (o.vibrato) {
    const [rate, depthCents] = o.vibrato;
    const lfo = k.osc('sine', rate, at, end + 0.05);
    for (const param of pitchParams) {
      const dg = k.gain(p0 * (2 ** (depthCents / 1200) - 1));
      lfo.connect(dg).connect(param);
    }
  }
  // Source shaping. A saw falls 6 dB per octave, so through a parallel formant bank the high
  // formants come out far too weak and the front vowels (e, i: F2 around 1.9–2.6 kHz) blur into
  // back ones. Beans get pre-emphasis (a high shelf, as formant synthesizers use) before a gentle
  // top roll-off; the announcer keeps its brighter, band-limited carrier.
  let tilt: AudioNode;
  if (o.carrier === 'robot') {
    tilt = k.lp(5200, 0.5);
    voiced.connect(tilt);
  } else {
    const shelf = k.ctx.createBiquadFilter();
    shelf.type = 'highshelf';
    shelf.frequency.value = PRE_EMPHASIS_HZ;
    shelf.gain.value = PRE_EMPHASIS_DB;
    tilt = k.lp(5500, 0.5);
    voiced.connect(shelf).connect(tilt);
  }

  const noise = k.noise('white', at, end + 0.05);

  // Formant bank (voiced + aspiration share it).
  const voiceAmp = k.gain(0);
  const aspAmp = k.gain(0);
  tilt.connect(voiceAmp);
  noise.connect(aspAmp);
  const sum = k.gain(1);
  const bands: BiquadFilterNode[] = [];
  const bandGains = [1, 0.62, 0.35];
  for (let i = 0; i < 3; i++) {
    const f = k.bp(ps[0].f[i] * fs, 6);
    voiceAmp.connect(f);
    aspAmp.connect(f);
    const g = k.gain(bandGains[i] * 2.4);
    f.connect(g).connect(sum);
    bands.push(f);
  }
  // Frication.
  const fricBand = k.bp(ps[0].fricHz, ps[0].fricQ);
  const fricAmp = k.gain(0);
  noise.connect(fricBand).connect(fricAmp).connect(sum);

  // Automation: hold each phone, glide into the next.
  let t = at;
  const BW = [90, 110, 170];
  for (const p of ps) {
    const reach = t + Math.min(p.glide, p.dur * 0.6);
    for (let i = 0; i < 3; i++) {
      const hz = p.f[i] * fs;
      bands[i].frequency.linearRampToValueAtTime(hz, reach);
      bands[i].Q.linearRampToValueAtTime(Math.max(2, hz / BW[i]), reach);
      bands[i].frequency.setValueAtTime(hz, t + p.dur);
      bands[i].Q.setValueAtTime(Math.max(2, hz / BW[i]), t + p.dur);
    }
    const amp = Math.min(0.012, p.dur * 0.3);
    voiceAmp.gain.linearRampToValueAtTime(p.voice, t + amp);
    voiceAmp.gain.setValueAtTime(p.voice, t + p.dur);
    aspAmp.gain.linearRampToValueAtTime(p.asp * 0.9, t + amp);
    aspAmp.gain.setValueAtTime(p.asp * 0.9, t + p.dur);
    fricAmp.gain.linearRampToValueAtTime(p.fric, t + Math.min(0.006, p.dur * 0.3));
    fricAmp.gain.setValueAtTime(p.fric, t + p.dur);
    fricBand.frequency.setValueAtTime(p.fricHz, t);
    fricBand.Q.setValueAtTime(p.fricQ, t);
    t += p.dur;
  }
  voiceAmp.gain.linearRampToValueAtTime(0, end + 0.02);
  aspAmp.gain.linearRampToValueAtTime(0, end + 0.02);
  fricAmp.gain.linearRampToValueAtTime(0, end + 0.02);

  const outEnv = k.env({ a: 0.006, d: 0.01, s: 1, hold: Math.max(0, total - 0.016), r: 0.03, peak: o.gain ?? 1, at });
  sum.connect(outEnv);
  if (o.carrier === 'robot') {
    // A little PA-horn colour: band-limited and gently driven.
    k.toOut(outEnv, k.hp(160), k.distort(0.18), k.lp(5500));
  } else {
    outEnv.connect(k.out);
  }
  return end + 0.05;
}
